import dayjs from 'dayjs'
import 'dayjs/locale/it'
import relativeTime from 'dayjs/plugin/relativeTime'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(relativeTime)
dayjs.extend(localizedFormat)
dayjs.extend(utc)
dayjs.extend(timezone)

/**
 * Format a date using locale-aware format
 */
export function formatDate(date: string | Date, format = 'LL'): string {
  return dayjs(date).format(format)
}

/**
 * Format a date with time using locale-aware format
 */
export function formatDateTime(date: string | Date): string {
  return dayjs(date).format('LLL')
}

/**
 * Format a date as relative time (e.g., "2 hours ago")
 * @param date - Date to format
 * @param locale - Optional locale ('it' or 'en')
 */
export function formatRelative(date: string | Date, locale?: 'it' | 'en'): string {
  const d = dayjs(date)
  if (locale) {
    return d.locale(locale).fromNow()
  }
  return d.fromNow()
}

/**
 * Set the locale for date formatting
 */
export function setLocale(locale: 'it' | 'en'): void {
  dayjs.locale(locale)
}

// ============================================================
// TIMEZONE-AWARE FORMATTING FUNCTIONS
// ============================================================

/**
 * Format a date in a specific timezone
 * @param date - Date to format (ISO string or Date object)
 * @param tz - IANA timezone identifier (e.g., 'Europe/Rome', 'America/New_York')
 * @param format - dayjs format string (default: 'LL' for locale-aware date)
 */
export function formatDateInTimezone(date: string | Date, tz: string, format = 'LL'): string {
  return dayjs(date).tz(tz).format(format)
}

/**
 * Format a date with time in a specific timezone
 * @param date - Date to format
 * @param tz - IANA timezone identifier
 */
export function formatDateTimeInTimezone(date: string | Date, tz: string): string {
  return dayjs(date).tz(tz).format('LLL')
}

/**
 * Format only the time portion in a specific timezone
 * @param date - Date to format
 * @param tz - IANA timezone identifier
 * @param format - Time format (default: 'HH:mm')
 */
export function formatTimeInTimezone(date: string | Date, tz: string, format = 'HH:mm'): string {
  return dayjs(date).tz(tz).format(format)
}

/**
 * Format a date with time and timezone indicator
 * @param date - Date to format
 * @param tz - IANA timezone identifier
 */
export function formatDateTimeWithTimezone(date: string | Date, tz: string): string {
  const formatted = dayjs(date).tz(tz).format('LLL')
  const offset = dayjs(date).tz(tz).format('Z')
  return `${formatted} (UTC${offset})`
}

/**
 * Get current date/time in a specific timezone
 * @param tz - IANA timezone identifier
 */
export function nowInTimezone(tz: string): dayjs.Dayjs {
  return dayjs().tz(tz)
}

export { dayjs }
